import {
  Model,
  BelongsToManySetAssociationsMixin,
} from 'sequelize';
import {
  IId,
  IName,
  Omit,
} from './util';
import {
  FileId,
  IApiFile,
  IApiFileUnparsed,
  IApiGetFile,
  IApiSetFile,
  IFileModel,
} from './file';
import {
  IApiGetLocation,
  LocationId,
  ILocationModel,
  IApiSetBookingLocation,
} from './location';
import { FILE_MEDIA_FIELDNAME_PREFIX } from '../constants';

export interface IApiBookingInputs {
  value: string;
  type: string;
  name: string;
  fileKey?: string;
}

export enum DisplayMode {
  Priority = 'PRIORITY',
  Standard = 'STANDARD',
  Fallback = 'FALLBACK',
  Category = 'CATEGORY',
}

interface IApiBookingBase extends IName {
  display_mode: DisplayMode;
  locations_group_name?: string | null;
  is_no_end: boolean;
  start_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  duration: number;
  external_id: string | null;
  category_id: number | null;
}

export interface IApiSetBooking extends IApiBookingBase {
  inputs: { [key: number]: IApiBookingInputs };
  is_daily: boolean;
  files: Array<IApiSetFile>;
  timezoneId: string;
  booking_template: number;
  locations: Array<IApiSetBookingLocation | number>;
  medias: object | string;
}

export interface IApiGetBooking extends IApiBookingBase, IId {
  inputs: Array<IApiBookingInputs>;
  is_daily: boolean;
  booking_template: IApiGetBookingTemplate;
  files: IApiGetFile[];
  locations: IApiGetLocation[];
  thumbnail_url: string | null;
  screenshot_url: string | null;
  category: IApiGetBooking;
}

export interface ISetBookingsProcessing extends IApiBookingBase {
  inputs: { [key: number]: IApiBookingInputs };
  id: number | string;
  booking_template: IApiGetBookingTemplate;
  files: IApiGetFile[];
  locations: IApiGetLocation[];
  thumbnail_url: string | null;
  screenshot_url: string | null;
}

export interface IApiSetBookingUnparsed extends IName {
  files: Array<IApiFileUnparsed>;
  display_mode: DisplayMode;
  locations_group_name?: string | null;
  data: string;
  start_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  timezoneId: string;
  booking_template: string;
  locations: string;
  duration: string;
  external_id: string | null;
  category_id: number | null;
  [FILE_MEDIA_FIELDNAME_PREFIX]?: string | undefined;
}

export interface IApiSetBookingTemplateInputs extends IName {
  type: string;
  label?: string;
  maxRows?: number;
  addColorButton?: boolean;
  presetColors?: string[];
  hidden?: boolean;
  default?: string | number | boolean;
  isRequired?: boolean;
  mimetypes?: string[];
  items?: string[];
  placeholder?: string;
  fileKey?: string;
  validate?: (data: any) => boolean;
}

export interface IApiGetBookingTemplateInputs extends IApiSetBookingTemplateInputs {
  id: number;
}

interface IApiBookingTemplateBase extends IName {
  category: string;
  duration_calculator: string;
  display_modes: Array<string> | null;
}

export interface IApiSetBookingTemplate extends IApiBookingTemplateBase {
  thumbnail: number;
  template: number;
  inputs_json: IApiSetBookingTemplateInputs[];
}

export interface IApiUpdateBookingTemplate extends IApiBookingTemplateBase {
  thumbnail?: number;
  template?: number;
  inputs_json: IApiSetBookingTemplateInputs[];
}

export interface IApiGetBookingTemplate extends IApiBookingTemplateBase, IId {
  inputs: IApiGetBookingTemplateInputs[];
  template: IApiFile;
  thumbnail: IApiFile;
}

export interface IApiGetSimpleBookingTemplate extends IId, IApiBookingTemplateBase {
  template: number;
  thumbnail: number;
}

export interface IApiSetBookingTemplateUnparsed extends IName {
  inputs: string;
  category: string;
  duration_calculator: string;
  files: IApiFileUnparsed[];
  display_modes: string;
}

export type BookingTemplateId = IId['id'];

export interface IBookingTemplateModel extends Model {
  id: BookingTemplateId;
  name: string | null;
  category: string | null;
  duration_calculator: string | null;
  thumbnail_id: FileId | null;
  template_id: FileId | null;
  inputs_json: string | null;
}

export type BookingTemplatePayload =
  Omit<IBookingTemplateModel, 'id' | 'inputs_json'>
  & { inputs_json: Array<IApiSetBookingTemplateInputs> | null }
  & { display_modes: Array<string> };

export type BookingId = IId['id'];

export interface IBookingModel extends Model {
  id: BookingId;
  name: string | null;
  display_mode: DisplayMode;
  locations_group_name: string | null;
  is_no_end: number | null;
  is_daily: number | null;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  duration: number | null;
  booking_template_id: BookingTemplateId | null;
  inputs: string | Array<IApiBookingInputs> | null;
  inputs_json: string | Array<IApiBookingInputs> | null;
  thumbnail_url: string | null;
  screenshot_url: string | null;
  external_id: string | null;
  category_id: number | null;
  status: string;
  setFiles: BelongsToManySetAssociationsMixin<IFileModel, number>;
  setLocations: BelongsToManySetAssociationsMixin<ILocationModel, number>;
}

export interface IBookingRowModel extends IBookingModel {
  files: IApiGetFile[];
  inputs_json: Array<IApiBookingInputs>;
}

export type BookingFileId = IId['id'];

export interface IBookingFileModel extends Model {
  id: BookingFileId;
  booking_id: BookingId;
  file_id: FileId;
}

export type BookingLocationId = IId['id'];

export interface IBookingLocationModel extends Model {
  id: BookingLocationId;
  booking_id: BookingId;
  location_id: LocationId;
}

export interface PreparedBookingData {
  booking_template: number;
  duration: number;
  end_date: string;
  end_time?: string;
  files: Array<{ file_id: { id: number; } }>;
  inputs_json: Array<{}>;
  is_daily: boolean;
  is_no_end: boolean;
  display_mode: DisplayMode;
  locations_group_name: string | null;
  locations: Array<{ location_id: { id: number; } }>;
  name: string;
  screenshot_url?: string;
  start_date: string;
  start_time?: string;
  thumbnail_url?: string;
  external_id: string | null;
  category_id: number | null;
}

export interface IBookingForCategory extends IApiBookingBase {
  locations: Array<{ id: number }>;
}
